import { useEffect } from "react";

// Normaliza createdAt del socket: devuelve el string tal como viene del backend
// (ISO con Z o naive sin TZ). La conversión a TZ de empresa la hace formatTime/
// parseNaiveDate en el momento del render, leyendo la TZ desde la BD.
// NO pre-convertir a naive local aquí — generaría doble-conversión errónea
// si parseNaiveDate del socket y del render tienen TZ distintas (stale closure).
const normalizeSocketMessageCreatedAt = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") return raw;
  if (raw instanceof Date) return raw.toISOString();
  return null;
};

/**
 * Gestiona los 4 listeners Socket.IO del componente MessagesList.
 * Invariante crítico: comparar ticketId como número Y como string para evitar
 * que "1305" !== 1305 bloquee mensajes en tiempo real.
 *
 * @param {object}   socketManager      — SocketContext
 * @param {*}        ticketId           — prop ticketId (primitivo)
 * @param {*}        ticketIdFromTicket — ticket?.id (primitivo, evita objeto en deps)
 * @param {object}   isMounted          — ref { current: bool } — DEBE venir del orquestador
 * @param {object}   currentTicketId    — ref { current: id } actualizado por el orquestador
 * @param {Function} dispatch           — useReducer dispatch (ADD_MESSAGE / UPDATE_MESSAGE)
 * @param {Function} parseNaiveDate     — de useTimezone()
 * @param {Function} scrollToBottom     — DOM scroll, definida en el orquestador
 */
const useMessagesListSocket = ({
  socketManager,
  ticketId,
  ticketIdFromTicket,
  isMounted,
  currentTicketId,
  dispatch,
  parseNaiveDate,
  scrollToBottom,
}) => {
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // ✅ CORRECCIÓN: Función para suscribirse al canal del ticket
    const subscribeToTicket = () => {
      // ✅ Usar ticketId prop O ticketIdFromTicket como fallback
      const effectiveTicketId = ticketId || ticketIdFromTicket || currentTicketId.current;
      if (effectiveTicketId) {
        socket.emit("joinChatBox", `${effectiveTicketId}`);
      }
    };

    // ✅ CORRECCIÓN: Suscribirse inmediatamente si el ticketId está disponible
    subscribeToTicket();

    // ✅ FIX 05/12/2025: Guardar referencias a los listeners para poder limpiarlos correctamente
    const handleReady = () => {
      subscribeToTicket();
    };

    const handleConnect = () => {
      // Re-suscribirse después de una reconexión
      subscribeToTicket();
    };

    // ✅ CORRECCIÓN: Suscribirse cuando el socket está ready
    socket.on("ready", handleReady);

    // ✅ CORRECCIÓN: Re-suscribirse cuando el socket se reconecta
    socket.on("connect", handleConnect);

    const handleAppMessage = (data) => {
      if (!isMounted.current) {
        return;
      }

      // ✅ CORRECCIÓN: Validar ticketId desde data.ticket O data.message
      const messageTicketId = data.message?.ticketId || data.ticket?.id;

      // ✅ CORRECCIÓN: Validar que el mensaje tenga propiedades mínimas necesarias
      if (!data.message || !data.message.id) {
        console.warn("[MessagesList] Mensaje recibido sin ID, ignorando:", data);
        return;
      }

      // ✅ CORRECCIÓN CRÍTICA: Verificar ticketId desde múltiples fuentes para evitar problemas de timing
      const currentTicketIdValue = currentTicketId.current || ticketIdFromTicket;

      // FIX: Comparar como número para evitar que string "1305" !== number 1305 (mensajes no se muestran en tiempo real)
      const idsMatch = messageTicketId && currentTicketIdValue &&
        (Number(messageTicketId) === Number(currentTicketIdValue) || String(messageTicketId) === String(currentTicketIdValue));

      if (idsMatch) {
        if (data.action === "update" && data.message) {
          const createdAtValue =
            normalizeSocketMessageCreatedAt(data.message.createdAt) ??
            data.message.createdAt ??
            null;

          const messageToUpdate = {
            ...data.message,
            ticketId: data.message.ticketId || messageTicketId,
            mediaType: data.message.mediaType || "conversation",
            channel: data.message.channel || "whatsapp",
            fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
            createdAt: createdAtValue
          };

          dispatch({ type: "UPDATE_MESSAGE", payload: messageToUpdate });
        } else if (data.action === "create" && data.message) {
          const createdAtValue =
            normalizeSocketMessageCreatedAt(data.message.createdAt) ??
            data.message.createdAt ??
            null;

          const messageToAdd = {
            ...data.message,
            ticketId: data.message.ticketId || messageTicketId,
            mediaType: data.message.mediaType || "conversation",
            channel: data.message.channel || "whatsapp",
            fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
            createdAt: createdAtValue
          };

          dispatch({ type: "ADD_MESSAGE", payload: messageToAdd });
          // ✅ Scroll automático al recibir nuevo mensaje
          setTimeout(() => scrollToBottom(), 100);
        }
      }
    };

    socket.on(`company-${companyId}-appMessage`, handleAppMessage);

    const handleAppMessageUpdate = (data) => {
      if (!isMounted.current) {
        return;
      }

      // ✅ CORRECCIÓN: Verificar ticketId desde múltiples fuentes
      const currentTicketIdValue = currentTicketId.current || ticketIdFromTicket || ticketId;
      const idsMatchUpdate = data.message?.ticketId && currentTicketIdValue &&
        (Number(data.message.ticketId) === Number(currentTicketIdValue) || String(data.message.ticketId) === String(currentTicketIdValue));
      if (idsMatchUpdate) {
        const createdAtValue =
          data.message?.createdAt != null
            ? normalizeSocketMessageCreatedAt(data.message.createdAt) ??
              data.message.createdAt
            : data.message.createdAt;

        dispatch({
          type: "UPDATE_MESSAGE",
          payload: {
            ...data.message,
            createdAt: createdAtValue,
          },
        });
      }
    };

    socket.on(`company-${companyId}-appMessage-update`, handleAppMessageUpdate);

    // ✅ CORRECCIÓN: Crear listener del canal del ticket solo si hay ticketId disponible
    const effectiveTicketIdForChannel = ticketId || ticketIdFromTicket;
    let ticketChannelListener = null;

    if (effectiveTicketIdForChannel) {
      ticketChannelListener = (data) => {
        if (!isMounted.current) {
          return;
        }

        // ✅ CORRECCIÓN: Procesar tanto create como update
        const messageTicketId = data.message?.ticketId || data.ticket?.id;

        // ✅ CORRECCIÓN: Validar que el mensaje tenga propiedades mínimas necesarias
        if (!data.message || !data.message.id) {
          console.warn("[MessagesList] Mensaje recibido sin ID en canal ticket, ignorando:", data);
          return;
        }

        // ✅ CORRECCIÓN: Verificar ticketId desde múltiples fuentes
        const currentTicketIdValue = currentTicketId.current || ticketIdFromTicket || ticketId;
        const idsMatchChannel = messageTicketId && currentTicketIdValue &&
          (Number(messageTicketId) === Number(currentTicketIdValue) || String(messageTicketId) === String(currentTicketIdValue));
        if (idsMatchChannel) {
          if (data.action === "update" && data.message) {
            const createdAtValue =
              normalizeSocketMessageCreatedAt(data.message.createdAt) ??
              data.message.createdAt ??
              null;

            const messageToUpdate = {
              ...data.message,
              ticketId: data.message.ticketId || messageTicketId,
              mediaType: data.message.mediaType || "conversation",
              channel: data.message.channel || "whatsapp",
              fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
              createdAt: createdAtValue
            };

            dispatch({ type: "UPDATE_MESSAGE", payload: messageToUpdate });
          } else if (data.action === "create" && data.message) {
            const createdAtValue =
              normalizeSocketMessageCreatedAt(data.message.createdAt) ??
              data.message.createdAt ??
              null;

            const messageToAdd = {
              ...data.message,
              ticketId: data.message.ticketId || messageTicketId,
              mediaType: data.message.mediaType || "conversation",
              channel: data.message.channel || "whatsapp",
              fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
              createdAt: createdAtValue
            };

            dispatch({ type: "ADD_MESSAGE", payload: messageToAdd });
            // ✅ Scroll automático al recibir nuevo mensaje
            setTimeout(() => scrollToBottom(), 100);
          }
        }
      };

      socket.on(`${effectiveTicketIdForChannel}`, ticketChannelListener);
    }

    return () => {
      if (socket && typeof socket.off === 'function') {
        const effectiveTicketId = ticketId || ticketIdFromTicket || currentTicketId.current;
        if (effectiveTicketId) socket.emit("leaveChatBox", `${effectiveTicketId}`);

        socket.off(`company-${companyId}-appMessage`, handleAppMessage);
        socket.off(`company-${companyId}-appMessage-update`, handleAppMessageUpdate);
        if (effectiveTicketIdForChannel && ticketChannelListener) {
          socket.off(`${effectiveTicketIdForChannel}`, ticketChannelListener);
        } else if (effectiveTicketId) {
          socket.off(`${effectiveTicketId}`);
        }
        socket.off("ready", handleReady);
        socket.off("connect", handleConnect);
        socket.off("disconnect");
      }
    };
    // ✅ FIX 05/12/2025: Cambiar dependencia de 'ticket' (objeto) a 'ticketIdFromTicket' (primitivo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, ticketIdFromTicket, socketManager]);
};

export default useMessagesListSocket;
